/**
 * Hora 360 (migrada do Streamlit; equivalência em backend/tests_paginas/test_hora360.py).
 *
 * A imagem é gerada no servidor (PNG idêntico ao do legado) e mostrada por URL; o botão
 * baixa o mesmo PNG. Tabela semanal (semana, mês e ano) ou mensal (mês, mês anterior e
 * ano). O tipo e as datas ficam na URL (vazias = padrões); as datas de mês e ano valem
 * para os dois tipos.
 */
import { Accordion, Alert, Button, Image } from '@mantine/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { CabecalhoPagina, Pagina, Secao } from '../../components/Layout';
import { AvisosApi } from '../../components/AvisosApi';
import { ErroApi, apiGet, montarUrl, type Envelope } from '../../api/cliente';
import { BarraFiltros, CampoData, CampoOpcoes, GrupoFiltros } from '../../components/Filtros';
import { TabelaDados, type TabelaApi } from '../../components/TabelaDados';
import { useFiltrosUrl } from '../../lib/url-state';
const CAMINHO = '/utilitarios/hora-360';
type Tipo = 'semanal' | 'mensal';
type Periodo = 'wtd' | 'mtd' | 'mant' | 'ytd';
type Campo = `${Periodo}_${'inicio' | 'fim'}`;
const TITULOS: Record<Periodo, string> = {
  wtd: 'Semana (WTD)',
  mtd: 'Mês (MTD)',
  mant: 'Mês anterior',
  ytd: 'Ano (YTD)',
};
// Mesma ordem das colunas da imagem (backend: hora360.PERIODOS).
const PERIODOS: Record<Tipo, Periodo[]> = {
  semanal: ['wtd', 'mtd', 'ytd'],
  mensal: ['mtd', 'mant', 'ytd'],
};
interface DadosHora360 {
  datas: Partial<Record<Campo, string>>;
  nome_arquivo: string;
  dados: TabelaApi;
}
export function Hora360() {
  const [filtros, definir] = useFiltrosUrl({
    tipo: 'semanal',
    wtd_inicio: '',
    wtd_fim: '',
    mtd_inicio: '',
    mtd_fim: '',
    mant_inicio: '',
    mant_fim: '',
    ytd_inicio: '',
    ytd_fim: '',
  });
  const tipo: Tipo = filtros.tipo === 'mensal' ? 'mensal' : 'semanal';
  const periodos = PERIODOS[tipo];
  // Só o tipo (se não for o padrão) e as datas dos períodos dele vão para a API.
  const parametros: Record<string, string | undefined> = {
    tipo: tipo === 'semanal' ? undefined : tipo,
    ...Object.fromEntries(
      periodos.flatMap((p) =>
        (['inicio', 'fim'] as const).map((lado) => {
          const campo: Campo = `${p}_${lado}`;
          return [campo, filtros[campo] || undefined];
        }),
      ),
    ),
  };
  const consulta = useQuery({
    queryKey: ['utilitarios.hora360', parametros],
    queryFn: ({ signal }) => apiGet<Envelope<DadosHora360>>(CAMINHO, parametros, signal),
    placeholderData: keepPreviousData,
  });
  const dados = consulta.data?.data;
  const urlImagem = montarUrl(`${CAMINHO}/imagem`, parametros);
  const [carregada, setCarregada] = useState<string | null>(null);
  const [erroImagem, setErroImagem] = useState<string | null>(null);
  return (
    <Pagina>
      <CabecalhoPagina
        titulo="Hora 360"
        descricao="Imagem-resumo das variações dos principais mercados: semanal (semana, mês e ano) ou mensal (mês, mês anterior e ano)."
        dadosAte={consulta.data?.meta.dados_ate}
        fonte="Banco Persevera (indicadores)"
        acoes={
          dados && (
            <Button
              component="a"
              color="dourado"
              href={montarUrl(`${CAMINHO}/imagem`, { ...parametros, download: 1 })}
            >
              Download da imagem
            </Button>
          )
        }
      />
      <BarraFiltros
        carregando={consulta.isFetching || (carregada !== urlImagem && erroImagem !== urlImagem)}
      >
        <CampoOpcoes
          rotulo="Tabela"
          valor={tipo}
          aoMudar={(v) => definir({ tipo: v })}
          opcoes={[
            { valor: 'semanal', rotulo: 'Semanal' },
            { valor: 'mensal', rotulo: 'Mensal' },
          ]}
        />
        {periodos.map((periodo) => (
          <GrupoFiltros key={periodo} titulo={TITULOS[periodo]}>
            {(
              [
                ['inicio', 'Início'],
                ['fim', 'Fim'],
              ] as const
            ).map(([lado, rotulo]) => {
              const campo: Campo = `${periodo}_${lado}`;
              return (
                <CampoData
                  key={campo}
                  rotulo={rotulo}
                  valor={filtros[campo] || dados?.datas[campo] || ''}
                  min="1990-01-01"
                  aoMudar={(data) => definir({ [campo]: data })}
                />
              );
            })}
          </GrupoFiltros>
        ))}
      </BarraFiltros>
      {consulta.isError && (
        <Alert color="red">
          {consulta.error instanceof ErroApi
            ? consulta.error.message
            : 'Não foi possível carregar os dados.'}
        </Alert>
      )}
      {dados && (
        <>
          <AvisosApi avisos={[consulta.data?.meta.avisos]} />
          <Secao
            titulo="Resumo de mercado"
            legenda="A mesma imagem do download (PNG), no tamanho original."
          >
            {erroImagem === urlImagem && (
              <Alert color="red">Não foi possível gerar a imagem.</Alert>
            )}
            <Image
              src={urlImagem}
              alt="Resumo de mercado (Hora 360)"
              // Tamanho natural do PNG (esticar borra o texto); encolhe em telas estreitas.
              // (sem alignSelf, o Stack — flex em coluna — estica a imagem até a largura toda).
              style={{ width: 'auto', maxWidth: '100%', alignSelf: 'flex-start' }}
              onLoad={() => setCarregada(urlImagem)}
              onError={() => setErroImagem(urlImagem)}
            />
          </Secao>
          <Secao titulo="Dados" legenda="Fechamentos usados no cálculo das variações.">
            <Accordion variant="contained">
              <Accordion.Item value="dados">
                <Accordion.Control>Visualizar dados</Accordion.Control>
                <Accordion.Panel>
                  <TabelaDados tabela={dados.dados} altura={420} />
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          </Secao>
        </>
      )}
    </Pagina>
  );
}
